import { useEffect, useState } from "react";
import { Button, Col, Container, Form, InputGroup, Modal, Row } from "react-bootstrap";
import { CompanyType } from "../../models/company";
import { checkPopupScript } from "../../utils/http/companies";
import AutomationServiceConfig from "./AutomationServiceConfig";

interface CompanyServicesProps {
  company: CompanyType;
  originalCompany: CompanyType | null;
  selectedService: string;
  setSelectedService: (service: string) => void;
  activeServices: string[];
  availableServices: string[];
  serviceStatuses: string[];
  hasServiceChanges: (serviceName: string) => boolean;
  hasFieldChanged: (currentValue: any, originalValue: any) => boolean;
  handleServiceSelect: (serviceName: string) => void;
  handleAddService: (serviceName: string) => void;
  handleServiceStatusChange: (serviceName: string, status: string) => void;
  handleServiceConfigChange: (serviceName: string, path: string[], value: any) => void;
  onDelete: () => void;
  ChangedIndicator: () => JSX.Element;
  readOnly?: boolean;
  activeAutomations: string[];
  availableAutomations: string[];
  automationStatuses: string[];
  hasAutomationChanges: (automationType: string) => boolean;
  handleAutomationConfigChange: (automationType: string, field: string, value: any) => void;
  handleAddAutomation: (automationType: string) => void;
  onDeleteAutomation: (automationType: string) => void;
  detailLoading?: boolean;
}

function CompanyServices({
  company,
  originalCompany,
  selectedService,
  activeServices,
  availableServices,
  serviceStatuses,
  hasServiceChanges,
  hasFieldChanged,
  handleServiceSelect,
  handleAddService,
  handleServiceStatusChange,
  handleServiceConfigChange,
  onDelete,
  ChangedIndicator,
  readOnly = false,
  activeAutomations,
  availableAutomations,
  automationStatuses,
  hasAutomationChanges,
  handleAutomationConfigChange,
  handleAddAutomation,
  onDeleteAutomation,
  detailLoading = false,
}: CompanyServicesProps) {
  const [showAddServiceModal, setShowAddServiceModal] = useState(false);
  const [showAddAutomationModal, setShowAddAutomationModal] = useState(false);
  const [validateUrlInput, setValidateUrlInput] = useState("");
  const [validateUrlResult, setValidateUrlResult] = useState<{
    type_page: string | null;
    show_popup: boolean;
  } | null>(null);
  const [validateUrlError, setValidateUrlError] = useState<string | null>(null);
  const [popupScriptStatus, setPopupScriptStatus] = useState<"idle" | "checking" | "exists" | "missing" | "unknown">(
    "idle",
  );
  const [popupScriptUrl, setPopupScriptUrl] = useState<string | null>(null);

  useEffect(() => {
    if (selectedService !== "popup" || !company?.name) {
      setPopupScriptStatus("idle");
      setPopupScriptUrl(null);
      return;
    }

    const controller = new AbortController();
    setPopupScriptStatus("checking");
    checkPopupScript({ companyNameOrId: company.name, signal: controller.signal })
      .then((result) => {
        setPopupScriptUrl(result.url);
        setPopupScriptStatus(result.status);
      })
      .catch((error) => {
        if (error?.name === "AbortError") return;
        setPopupScriptStatus("unknown");
      });

    return () => {
      controller.abort();
    };
  }, [selectedService, company?.name]);

  const handleAdd = (serviceName: string) => {
    handleAddService(serviceName);
    setShowAddServiceModal(false);
  };

  const handleAddAutomationClick = (automationType: string) => {
    handleAddAutomation(automationType);
    setShowAddAutomationModal(false);
  };

  return (
    <>
      <Container fluid>
        <Row>
          <Col md={3} className="border-end">
            <div className="list-group">
              {activeServices.map((serviceName) => {
                const serviceConfig = company?.services?.[serviceName] || {};
                const status = serviceConfig.status || "inactive";
                return (
                  <button
                    key={serviceName}
                    type="button"
                    className={`list-group-item list-group-item-action d-flex justify-content-between align-items-center ${selectedService === serviceName ? "active" : ""}`}
                    onClick={() => handleServiceSelect(serviceName)}
                  >
                    <span className="text-capitalize">
                      {serviceName}
                      {hasServiceChanges(serviceName) && <ChangedIndicator />}
                    </span>
                    <span
                      className={`badge ${
                        status === "active" ? "bg-success" : status === "onboarding" ? "bg-warning" : "bg-secondary"
                      }`}
                    >
                      {status}
                    </span>
                  </button>
                );
              })}
              {availableServices.length > 0 && !readOnly && (
                <button
                  type="button"
                  className="list-group-item list-group-item-action text-center"
                  onClick={() => setShowAddServiceModal(true)}
                  style={{ color: "#0d6efd", fontWeight: "500" }}
                >
                  <i className="bi bi-plus-circle me-2"></i>
                  Add Service
                </button>
              )}
            </div>
          </Col>
          <Col md={9}>
            {detailLoading ? (
              <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "200px" }}>
                <div className="spinner-border text-secondary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : selectedService &&
              (() => {
                const serviceConfig = company?.services?.[selectedService] || {};
                const originalServiceConfig = originalCompany?.services?.[selectedService] || {};
                const currentStatus = serviceConfig.status || "inactive";

                const triggersValue =
                  typeof serviceConfig.triggers === "string"
                    ? serviceConfig.triggers
                    : serviceConfig.triggers != null
                      ? JSON.stringify(serviceConfig.triggers, null, 2)
                      : "";
                let triggersError: string | null = null;
                if (triggersValue.trim() !== "") {
                  try {
                    JSON.parse(triggersValue);
                  } catch {
                    triggersError = "Invalid JSON";
                  }
                }

                const handleValidateUrl = () => {
                  setValidateUrlResult(null);
                  setValidateUrlError(null);

                  let triggers: Record<string, unknown>;
                  try {
                    triggers = triggersValue.trim() === "" ? {} : JSON.parse(triggersValue);
                  } catch {
                    setValidateUrlError("Invalid JSON in Triggers");
                    return;
                  }

                  try {
                    for (const [key, values] of Object.entries(triggers)) {
                      const pattern = new RegExp("(" + (values as string[]).join(")|(") + ")");
                      if (pattern.test(validateUrlInput)) {
                        setValidateUrlResult({ type_page: key, show_popup: true });
                        return;
                      }
                    }
                    setValidateUrlResult({ type_page: null, show_popup: false });
                  } catch {
                    setValidateUrlError("Invalid regex pattern in Triggers");
                  }
                };

                return (
                  <div>
                    <div className="d-flex justify-content-between align-items-center mb-4">
                      <h4 className="text-capitalize mb-0">{selectedService} Configuration</h4>
                      {!readOnly && (
                        <div className="d-flex gap-2">
                          <Button variant="outline-danger" onClick={onDelete} size="sm">
                            <i className="bi bi-trash me-2"></i>
                            Delete Service
                          </Button>
                          {selectedService === "automation" && availableAutomations.length > 0 && (
                            <Button variant="outline-primary" onClick={() => setShowAddAutomationModal(true)} size="sm">
                              <i className="bi bi-plus-circle me-2"></i>
                              Add Automation
                            </Button>
                          )}
                        </div>
                      )}
                    </div>

                    {selectedService === "popup" && (
                      <div className="alert alert-warning" role="alert">
                        <div className="d-flex">
                          <i className="bi bi-exclamation-triangle me-2 mt-1"></i>
                          <div>
                            Make sure the popup script is deployed in the proper bucket, and that the popup is enabled
                            manually within the popup script as well. See{" "}
                            <a
                              href="https://www.notion.so/Analysis-Website-Popups-23016e8366e5802f80a7ec2ed2039f00?source=copy_link"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              this Notion page
                            </a>{" "}
                            for a more elaborate explanation.
                            {popupScriptUrl && (
                              <div className="mt-2">
                                Popup script:{" "}
                                <a href={popupScriptUrl} target="_blank" rel="noopener noreferrer">
                                  {popupScriptUrl}
                                </a>
                                <div className="mt-1">
                                  {popupScriptStatus === "checking" && (
                                    <span className="text-muted">
                                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                      Checking if the script exists…
                                    </span>
                                  )}
                                  {popupScriptStatus === "exists" && (
                                    <span className="text-success">
                                      <i className="bi bi-check-circle me-1"></i>
                                      Script found in the bucket.
                                    </span>
                                  )}
                                  {popupScriptStatus === "missing" && (
                                    <span className="text-danger">
                                      <i className="bi bi-x-circle me-1"></i>
                                      Script not found (404) — it still needs to be deployed to the bucket.
                                    </span>
                                  )}
                                  {popupScriptStatus === "unknown" && (
                                    <span className="text-muted">
                                      <i className="bi bi-question-circle me-1"></i>
                                      Could not verify automatically — open the link above to check.
                                    </span>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    <Form.Group className="mb-4">
                      <Form.Label>
                        Status
                        {hasFieldChanged(serviceConfig.status, originalServiceConfig.status) && <ChangedIndicator />}
                      </Form.Label>
                      <Form.Select
                        value={currentStatus}
                        onChange={(e) => handleServiceStatusChange(selectedService, e.target.value)}
                        disabled={readOnly}
                      >
                        {serviceStatuses.map((status) => (
                          <option key={status} value={status}>
                            {status}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>

                    {selectedService === "popup" && (
                      <div className="mb-4">
                        <h5 className="mb-3">
                          Triggers
                          {hasFieldChanged(serviceConfig.triggers, originalServiceConfig.triggers) && (
                            <ChangedIndicator />
                          )}
                        </h5>
                        <div className="alert alert-info d-flex align-items-center" role="alert">
                          <i className="bi bi-info-circle me-2"></i>
                          Caching may apply, so changes can take up to 5 minutes to take effect.
                        </div>
                        <Form.Group>
                          <Form.Control
                            as="textarea"
                            rows={8}
                            value={triggersValue}
                            onChange={(e) => handleServiceConfigChange(selectedService, ["triggers"], e.target.value)}
                            isInvalid={!!triggersError}
                            readOnly={readOnly}
                            style={{ fontFamily: "monospace", fontSize: "14px" }}
                            placeholder="{}"
                          />
                          <Form.Control.Feedback type="invalid">{triggersError}</Form.Control.Feedback>
                        </Form.Group>

                        <div className="mt-3">
                          <Form.Label>Validate URL</Form.Label>
                          <InputGroup>
                            <Form.Control
                              type="text"
                              value={validateUrlInput}
                              onChange={(e) => setValidateUrlInput(e.target.value)}
                              placeholder="https://example.com/gratis-schatting/"
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  e.preventDefault();
                                  handleValidateUrl();
                                }
                              }}
                            />
                            <Button variant="outline-primary" onClick={handleValidateUrl} disabled={!!triggersError}>
                              Validate URL
                            </Button>
                          </InputGroup>
                          {validateUrlError && <div className="text-danger mt-2">{validateUrlError}</div>}
                          {validateUrlResult && (
                            <pre
                              className={`mt-2 p-2 border rounded mb-0 ${
                                validateUrlResult.show_popup ? "bg-success-subtle" : "bg-body-secondary"
                              }`}
                              style={{ fontFamily: "monospace", fontSize: "14px" }}
                            >
                              {JSON.stringify(validateUrlResult, null, 2)}
                            </pre>
                          )}
                        </div>
                      </div>
                    )}

                    {selectedService === "popup" && currentStatus !== "inactive" && (
                      <div>
                        <h5 className="mb-3">Forward Methods</h5>
                        {["era", "whise", "email", "webhook", "skarabee"].map((method) => {
                          const methodConfig = serviceConfig.forward_methods?.[method] || {};
                          return (
                            <div key={method} className="mb-3 p-3 border rounded">
                              <Form.Check
                                type="checkbox"
                                label={<strong className="text-capitalize">{method}</strong>}
                                checked={methodConfig.enabled || false}
                                onChange={(e) =>
                                  handleServiceConfigChange(
                                    selectedService,
                                    ["forward_methods", method, "enabled"],
                                    e.target.checked,
                                  )
                                }
                                disabled={readOnly}
                              />
                              {method === "webhook" && methodConfig.enabled && (
                                <Form.Group className="mt-2">
                                  <Form.Label>Webhook URL</Form.Label>
                                  <Form.Control
                                    type="url"
                                    value={methodConfig.url || ""}
                                    onChange={(e) =>
                                      handleServiceConfigChange(
                                        selectedService,
                                        ["forward_methods", method, "url"],
                                        e.target.value,
                                      )
                                    }
                                    readOnly={readOnly}
                                  />
                                </Form.Group>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {selectedService === "automation" && currentStatus !== "inactive" && (
                      <AutomationServiceConfig
                        company={company}
                        originalCompany={originalCompany}
                        activeAutomations={activeAutomations}
                        availableAutomations={availableAutomations}
                        automationStatuses={automationStatuses}
                        hasAutomationChanges={hasAutomationChanges}
                        hasFieldChanged={hasFieldChanged}
                        handleServiceConfigChange={handleServiceConfigChange}
                        handleAutomationConfigChange={handleAutomationConfigChange}
                        handleAddAutomation={handleAddAutomation}
                        onDeleteAutomation={onDeleteAutomation}
                        ChangedIndicator={ChangedIndicator}
                        readOnly={readOnly}
                      />
                    )}

                    {(selectedService === "dashboard" || selectedService === "tracker") &&
                      currentStatus !== "inactive" && (
                        <div className="alert alert-info">No additional configuration required for this service.</div>
                      )}
                  </div>
                );
              })()}
          </Col>
        </Row>
      </Container>

      {/* Add Service Modal */}
      <Modal show={showAddServiceModal} onHide={() => setShowAddServiceModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Add Service</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Select a service to add:</p>
          <div className="list-group">
            {availableServices.map((serviceName) => (
              <button
                key={serviceName}
                type="button"
                className="list-group-item list-group-item-action text-capitalize"
                onClick={() => handleAdd(serviceName)}
              >
                {serviceName}
              </button>
            ))}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAddServiceModal(false)}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Add Automation Modal */}
      <Modal show={showAddAutomationModal} onHide={() => setShowAddAutomationModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Add Automation</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Select an automation to add:</p>
          <div className="list-group">
            {availableAutomations.map((automationType) => (
              <button
                key={automationType}
                type="button"
                className="list-group-item list-group-item-action text-capitalize"
                onClick={() => handleAddAutomationClick(automationType)}
              >
                {automationType}
              </button>
            ))}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAddAutomationModal(false)}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default CompanyServices;
